import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GameController, Heart, ShoppingBag, Star, Eye, MagnifyingGlass,
  Plus, Trash, PencilSimple, GraduationCap, Backpack, ArrowsClockwise,
  ListBullets, SlidersHorizontal, CaretDown, X, Check, HandWaving, Barcode
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import { ContextualTour, EmptyStateCard } from '../components/ContextualEducation';
import { BarcodeScannerModal } from '../components/BarcodeScanner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  owned: { label: 'Owned', color: '#10B981', icon: GameController },
  favorite: { label: 'Favorite', color: '#F59E0B', icon: Heart },
  wishlist: { label: 'Wishlist', color: '#9D4CDD', icon: ShoppingBag },
  interested: { label: 'Interested', color: '#3B82F6', icon: Eye },
  played: { label: 'Played', color: '#06B6D4', icon: Star },
  mastered: { label: 'Mastered', color: '#EC4899', icon: GraduationCap },
  trading: { label: 'Trading', color: '#F97316', icon: ArrowsClockwise },
  borrowed: { label: 'Borrowed', color: '#8B5CF6', icon: HandWaving },
  no_longer_owned: { label: 'Past', color: '#6B7280', icon: Trash },
};

const SKILL_LABELS = { beginner: 'Beginner', familiar: 'Familiar', experienced: 'Experienced', expert: 'Expert' };
const FREQ_LABELS = { rarely: 'Rarely', sometimes: 'Sometimes', often: 'Often', favorite_rotation: 'Favorite' };

export default function MyCollectionPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [gameSearch, setGameSearch] = useState('');
  const [addStatus, setAddStatus] = useState('owned');
  const [addVisibility, setAddVisibility] = useState('public');
  const [showScanner, setShowScanner] = useState(false);
  // "Did you mean?" duplicate-suggestion modal.
  const [dupSuggestions, setDupSuggestions] = useState(null); // null | { name, suggestions: [...] }
  const [editingId, setEditingId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const [colRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/collections/me${params}`, { withCredentials: true }),
        axios.get(`${API}/api/collections/stats/me`, { withCredentials: true }),
      ]);
      setItems(colRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Load a default browse-list of popular games on modal open, so users don't
  // have to type anything to see something clickable.
  const loadBrowseGames = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/games-browse?limit=30`, { withCredentials: true });
      setGames(Array.isArray(res.data) ? res.data : []);
    } catch { setGames([]); }
  }, []);

  const searchGames = useCallback(async (q) => {
    // Empty / short query → show the browse-list, not a blank state.
    if (!q || q.length < 2) { loadBrowseGames(); return; }
    try {
      const res = await axios.get(`${API}/api/games-browse?search=${encodeURIComponent(q)}&limit=30`, { withCredentials: true });
      setGames(res.data);
    } catch (e) { setGames([]); }
  }, [loadBrowseGames]);

  useEffect(() => {
    const t = setTimeout(() => searchGames(gameSearch), 300);
    return () => clearTimeout(t);
  }, [gameSearch, searchGames]);

  // Pre-load browse-list when the modal opens so users see games immediately.
  useEffect(() => {
    if (showAdd && !gameSearch) loadBrowseGames();
  }, [showAdd, gameSearch, loadBrowseGames]);

  const addToCollection = async (gameId) => {
    try {
      await axios.post(`${API}/api/collections`, {
        game_id: gameId, collection_status: addStatus, visibility: addVisibility,
      }, { withCredentials: true });
      toast.success('Added to collection!');
      setShowAdd(false);
      setGameSearch('');
      setGames([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    }
  };

  const addCustomGame = async ({ skipDupCheck = false } = {}) => {
    if (!gameSearch.trim()) return;
    // Duplicate detection — hit the preview endpoint first so we can suggest
    // an existing catalog game before creating a fresh moderation-queue item.
    if (!skipDupCheck) {
      try {
        const { data } = await axios.post(
          `${API}/api/collections/custom-game-check`,
          { name: gameSearch.trim() },
          { withCredentials: true }
        );
        const sugg = data?.suggestions || [];
        if (sugg.length) {
          setDupSuggestions({ name: gameSearch.trim(), suggestions: sugg });
          return; // Wait for user to pick from modal.
        }
      } catch { /* soft fail — proceed to create */ }
    }
    try {
      const { data } = await axios.post(`${API}/api/collections`, {
        custom_game_name: gameSearch.trim(), collection_status: addStatus, visibility: addVisibility,
      }, { withCredentials: true });
      const isPending = data?.game_moderation_status === 'pending' || data?.game_user_submitted;
      if (isPending) {
        toast.success(`"${gameSearch.trim()}" added — pending admin review before others can find it.`, { duration: 5000 });
      } else {
        toast.success(`Added "${gameSearch.trim()}" to collection!`);
      }
      setShowAdd(false);
      setGameSearch('');
      setGames([]);
      setDupSuggestions(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to add';
      toast.error(typeof msg === 'string' ? msg : 'Failed to add');
    }
  };

  const handleScannedGame = async ({ game_name, game_id }) => {
    try {
      const payload = game_id ? { game_id, collection_status: 'owned' } : { custom_game_name: game_name, collection_status: 'owned' };
      await axios.post(`${API}/api/collections`, payload, { withCredentials: true });
      toast.success(`Added "${game_name}" to collection!`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    }
  };

  const updateItem = async (id, data) => {
    try {
      await axios.put(`${API}/api/collections/${id}`, data, { withCredentials: true });
      toast.success('Updated!');
      setEditingId(null);
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  const updateDetails = async (id, data) => {
    try {
      await axios.put(`${API}/api/collections/${id}/details`, data, { withCredentials: true });
      toast.success('Details updated!');
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  const removeItem = async (id) => {
    try {
      await axios.delete(`${API}/api/collections/${id}`, { withCredentials: true });
      toast.success('Removed from collection');
      load();
    } catch (e) { toast.error('Remove failed'); }
  };

  const filtered = items.filter(i =>
    !search || (i.game_name && i.game_name.toLowerCase().includes(search.toLowerCase()))
  );

  const tabs = [
    { key: 'all', label: 'All', count: stats?.total || 0 },
    { key: 'owned', label: 'Owned', count: stats?.owned || 0 },
    { key: 'favorite', label: 'Favorites', count: stats?.favorites || 0 },
    { key: 'wishlist', label: 'Wishlist', count: stats?.wishlist || 0 },
    { key: 'played', label: 'Played', count: stats?.played || 0 },
  ];

  return (
    <ContextualTour tourKey="collection_page">
    <div className="min-h-screen bg-[#06060A] pb-24" data-testid="my-collection-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#10B981]/8 to-transparent px-4 pt-6 pb-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>My Collection</h1>
              <p className="text-sm text-[#777] mt-1">
                {stats?.total || 0} games · {stats?.willing_to_bring || 0} can bring · {stats?.willing_to_teach || 0} can teach
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowScanner(true)}
                className="bg-[#9D4CDD]/10 hover:bg-[#9D4CDD]/20 text-[#9D4CDD] font-semibold rounded-full px-3 h-9 border border-[#9D4CDD]/20"
                data-testid="scan-game-btn"
              >
                <Barcode size={16} weight="bold" />
              </Button>
              <Button
                onClick={() => setShowAdd(true)}
                className="bg-[#10B981] hover:bg-[#10B981]/80 text-black font-semibold rounded-full px-4 h-9"
                data-testid="add-game-btn"
              >
                <Plus size={16} weight="bold" className="mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search your collection..."
              className="w-full bg-[#0e0e16] border border-white/[0.06] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#10B981]/40"
              data-testid="collection-search"
            />
          </div>

          {/* Filter Tabs — chip style, color matches status accent.
              Active chip uses the status's signature color for instant visual
              recognition (e.g. orange for Favorites, purple for Wishlist). */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 hide-scrollbar">
            {tabs.map(t => {
              const cfg = STATUS_CONFIG[t.key];
              const accent = cfg?.color || '#10B981';
              const active = filter === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setFilter(t.key)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all inline-flex items-center gap-1.5"
                  style={active ? {
                    backgroundColor: accent,
                    color: '#0a0a0f',
                  } : {
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    color: '#888',
                  }}
                  data-testid={`filter-${t.key}`}
                >
                  {cfg?.icon ? <cfg.icon size={12} weight={active ? 'fill' : 'regular'} /> : null}
                  {t.label} <span className="opacity-70">({t.count})</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Collection Items */}
      <div className="px-4 max-w-2xl mx-auto">
        {loading ? (
          <div className="text-center py-12 text-[#555]">Loading collection...</div>
        ) : filtered.length === 0 ? (
          items.length === 0 ? (
            <EmptyStateCard type="collections" onAction={(a) => a === 'add_game' && setShowAdd(true)} />
          ) : (
            <div className="text-center py-16">
              <GameController size={48} className="mx-auto text-[#333] mb-3" />
              <p className="text-[#666] text-sm">No matches found.</p>
            </div>
          )
        ) : (
          <div className="space-y-2">
            {filtered.map(item => {
              const cfg = STATUS_CONFIG[item.collection_status] || STATUS_CONFIG.owned;
              const Icon = cfg.icon;
              const details = item.details;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0c0c14] border border-white/[0.06] rounded-lg overflow-hidden"
                  data-testid={`collection-item-${item.id}`}
                >
                  <div className="p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${cfg.color}15` }}>
                      <Icon size={20} weight="duotone" style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-white font-medium text-sm truncate">{item.game_name || 'Unknown Game'}</p>
                        {item.game_orphaned && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#EF4444]/15 text-[#EF4444] font-semibold uppercase tracking-wider shrink-0" data-testid={`orphan-badge-${item.id}`}>Removed</span>
                        )}
                        {!item.game_orphaned && item.game_moderation_status === 'pending' && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] font-semibold uppercase tracking-wider shrink-0" data-testid={`pending-badge-${item.id}`}>Pending review</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${cfg.color}20`, color: cfg.color }}>
                          {cfg.label}
                        </span>
                        {item.game_category && <span className="text-[10px] text-[#555]">{item.game_category}</span>}
                        {item.game_complexity && <span className="text-[10px] text-[#555]">· {item.game_complexity}</span>}
                        {details?.bring_to_events && <Backpack size={12} className="text-[#10B981]" />}
                        {details?.preferred_to_teach && <GraduationCap size={12} className="text-[#F59E0B]" />}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setDetailsOpen(detailsOpen === item.id ? null : item.id)}
                        className="p-1.5 rounded-lg hover:bg-white/[0.06] text-[#666] transition-colors"
                        data-testid={`details-toggle-${item.id}`}
                      >
                        <SlidersHorizontal size={16} />
                      </button>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#666] hover:text-red-400 transition-colors"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Expandable Details */}
                  <AnimatePresence>
                    {detailsOpen === item.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                        className="border-t border-white/[0.04] overflow-hidden"
                      >
                        <div className="p-3 grid grid-cols-2 gap-2">
                          {/* Status */}
                          <div>
                            <label className="text-[10px] text-[#555] uppercase tracking-wider">Status</label>
                            <select
                              value={item.collection_status}
                              onChange={e => updateItem(item.id, { collection_status: e.target.value })}
                              className="w-full mt-1 bg-[#0a0a12] border border-white/[0.06] rounded text-xs text-white p-1.5"
                              data-testid={`status-select-${item.id}`}
                            >
                              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                              ))}
                            </select>
                          </div>
                          {/* Visibility */}
                          <div>
                            <label className="text-[10px] text-[#555] uppercase tracking-wider">Visibility</label>
                            <select
                              value={item.visibility}
                              onChange={e => updateItem(item.id, { visibility: e.target.value })}
                              className="w-full mt-1 bg-[#0a0a12] border border-white/[0.06] rounded text-xs text-white p-1.5"
                              data-testid={`visibility-select-${item.id}`}
                            >
                              <option value="public">Public</option>
                              <option value="friends_only">Friends Only</option>
                              <option value="private">Private</option>
                            </select>
                          </div>
                          {/* Skill Level */}
                          <div>
                            <label className="text-[10px] text-[#555] uppercase tracking-wider">Skill Level</label>
                            <select
                              value={details?.skill_level || ''}
                              onChange={e => updateDetails(item.id, { skill_level: e.target.value })}
                              className="w-full mt-1 bg-[#0a0a12] border border-white/[0.06] rounded text-xs text-white p-1.5"
                              data-testid={`skill-select-${item.id}`}
                            >
                              <option value="">Not set</option>
                              {Object.entries(SKILL_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                              ))}
                            </select>
                          </div>
                          {/* Play Frequency */}
                          <div>
                            <label className="text-[10px] text-[#555] uppercase tracking-wider">Play Frequency</label>
                            <select
                              value={details?.play_frequency || ''}
                              onChange={e => updateDetails(item.id, { play_frequency: e.target.value })}
                              className="w-full mt-1 bg-[#0a0a12] border border-white/[0.06] rounded text-xs text-white p-1.5"
                              data-testid={`freq-select-${item.id}`}
                            >
                              <option value="">Not set</option>
                              {Object.entries(FREQ_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                              ))}
                            </select>
                          </div>
                          {/* Toggles */}
                          <div className="col-span-2 flex flex-wrap gap-2 mt-1">
                            <button
                              onClick={() => updateDetails(item.id, { bring_to_events: !(details?.bring_to_events) })}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${
                                details?.bring_to_events ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-white/[0.04] text-[#666]'
                              }`}
                              data-testid={`bring-toggle-${item.id}`}
                            >
                              <Backpack size={12} /> Can Bring
                            </button>
                            <button
                              onClick={() => updateDetails(item.id, { preferred_to_teach: !(details?.preferred_to_teach) })}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${
                                details?.preferred_to_teach ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-white/[0.04] text-[#666]'
                              }`}
                              data-testid={`teach-toggle-${item.id}`}
                            >
                              <GraduationCap size={12} /> Can Teach
                            </button>
                            <button
                              onClick={() => updateDetails(item.id, { preferred_to_play: !(details?.preferred_to_play) })}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${
                                details?.preferred_to_play ? 'bg-[#3B82F6]/20 text-[#3B82F6]' : 'bg-white/[0.04] text-[#666]'
                              }`}
                              data-testid={`play-toggle-${item.id}`}
                            >
                              <GameController size={12} /> Want to Play
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Game Modal */}
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center"
            onClick={e => e.target === e.currentTarget && setShowAdd(false)}
          >
            <motion.div
              initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
              className="bg-[#0e0e16] border border-white/[0.06] rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden"
              data-testid="add-game-modal"
            >
              <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Add Game</h3>
                <button onClick={() => setShowAdd(false)} className="text-[#666] hover:text-white"><X size={20} /></button>
              </div>
              <div className="p-4 space-y-3">
                {/* Game Search */}
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                  <input
                    value={gameSearch} onChange={e => setGameSearch(e.target.value)}
                    placeholder="Search or type your own game name..."
                    autoFocus
                    className="w-full bg-[#0a0a12] border border-white/[0.06] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#10B981]/40"
                    data-testid="game-search-input"
                  />
                </div>

                {/* Persistent "Add my own game" CTA — sits ABOVE the scroll area so
                    it can never get lost. Enabled the moment the user types 2+ chars.
                    Christopher's Feb 2026 report: this used to be buried at the
                    bottom of the results list; now it's the first thing after the
                    search bar and remains stationary while the game list scrolls. */}
                <button
                  onClick={addCustomGame}
                  disabled={gameSearch.trim().length < 2}
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-[#9D4CDD]/15 to-[#00F0FF]/10 border border-[#9D4CDD]/30 hover:from-[#9D4CDD]/25 hover:to-[#00F0FF]/15 transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="add-own-game-cta"
                >
                  <div className="w-9 h-9 rounded bg-[#9D4CDD]/25 flex items-center justify-center shrink-0">
                    <Plus size={18} weight="bold" className="text-[#9D4CDD]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-semibold truncate">
                      {gameSearch.trim().length >= 2 ? `Add "${gameSearch.trim()}" to my collection` : 'Add my own game'}
                    </p>
                    <p className="text-[#B47CE8] text-[10px]">
                      Not in our database? We&rsquo;ll create it. Type at least 2 letters above.
                    </p>
                  </div>
                </button>

                {/* Status & Visibility */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-[#555] uppercase tracking-wider mb-1 block">Status</label>
                    <select value={addStatus} onChange={e => setAddStatus(e.target.value)}
                      className="w-full bg-[#0a0a12] border border-white/[0.06] rounded text-xs text-white p-2"
                      data-testid="add-status-select"
                    >
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-[#555] uppercase tracking-wider mb-1 block">Visibility</label>
                    <select value={addVisibility} onChange={e => setAddVisibility(e.target.value)}
                      className="w-full bg-[#0a0a12] border border-white/[0.06] rounded text-xs text-white p-2"
                      data-testid="add-visibility-select"
                    >
                      <option value="public">Public</option>
                      <option value="friends_only">Friends Only</option>
                      <option value="private">Private</option>
                    </select>
                  </div>
                </div>
                {/* Results — browse-first (shows on modal open) + search-narrowed */}
                <div className="max-h-[40vh] overflow-y-auto space-y-1 -mx-1 px-1" data-testid="game-results-list">
                  {gameSearch.trim().length < 2 && games.length > 0 && (
                    <p className="text-[10px] uppercase tracking-wider text-[#555] font-semibold px-1 pt-1 pb-2">Popular Games — tap to add</p>
                  )}
                  {games.map(g => (
                    <button
                      key={g.id}
                      onClick={() => addToCollection(g.id)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/[0.04] transition-colors text-left"
                      data-testid={`add-game-${g.slug}`}
                    >
                      <div className="w-8 h-8 rounded bg-[#10B981]/10 flex items-center justify-center shrink-0">
                        <GameController size={16} className="text-[#10B981]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{g.name}</p>
                        <p className="text-[#555] text-[10px]">{g.publisher} · {g.min_players}-{g.max_players}p · {g.complexity}</p>
                      </div>
                    </button>
                  ))}
                  {gameSearch.length >= 2 && games.length === 0 && (
                    <div className="text-center py-3">
                      <p className="text-[#555] text-xs">No matching games in our database — use the purple button above to add your own.</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BarcodeScannerModal open={showScanner} onClose={() => setShowScanner(false)} onGameFound={handleScannedGame} />

      {/* Duplicate-suggestion modal — shows fuzzy matches from the catalog before
          the user's submission enters the admin moderation queue. Keeps the
          catalog clean and saves the submitter a wait for approval. */}
      {dupSuggestions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setDupSuggestions(null)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-[#0e0e16] border border-white/[0.08] rounded-2xl p-5" data-testid="dup-suggestion-modal">
            <h2 className="text-white text-base font-bold mb-1">Did you mean one of these?</h2>
            <p className="text-[12px] text-[#A1A1AA] mb-3">
              We already have games with similar names in the catalog. Adding an existing entry is instant — no admin wait.
            </p>
            <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
              {dupSuggestions.suggestions.map(s => (
                <button
                  key={s.id}
                  onClick={() => addToCollection(s.id)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.03] hover:bg-[#10B981]/10 border border-transparent hover:border-[#10B981]/30 transition-colors text-left"
                  data-testid={`dup-match-${s.slug || s.id}`}
                >
                  <div className="w-8 h-8 rounded bg-[#10B981]/15 flex items-center justify-center shrink-0">
                    <GameController size={16} className="text-[#10B981]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-semibold truncate">{s.name}</p>
                    <p className="text-[#555] text-[10px]">
                      {s.publisher || 'unknown publisher'}
                      {s.min_players ? ` · ${s.min_players}-${s.max_players}p` : ''}
                      {' · '}<span className="text-[#10B981]/70">{Math.round((s.score || 0) * 100)}% match</span>
                    </p>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-4 pt-3 border-t border-white/[0.06]">
              <button onClick={() => setDupSuggestions(null)}
                className="flex-1 py-2 rounded-md bg-white/5 text-[#A1A1AA] text-xs font-medium hover:bg-white/10"
                data-testid="dup-cancel">
                Cancel
              </button>
              <button onClick={() => addCustomGame({ skipDupCheck: true })}
                className="flex-1 py-2 rounded-md bg-[#9D4CDD] text-white text-xs font-semibold hover:bg-[#8B3DC7]"
                data-testid="dup-continue-anyway">
                None match — add mine anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
    </ContextualTour>
  );
}
